import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { setGlobalAuthToken, apiRequest } from '../lib/api';

type UserRole = 'super_admin' | 'admin' | 'marketing_collaborator' | 'user' | null;

interface AdminAuthContextValue {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  walletAddress: string | null;
  role: UserRole;
  isLoading: boolean;
  error: string | null;
  logout: () => Promise<void>;
  refreshAdminStatus: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

interface AdminAuthProviderProps {
  children: ReactNode;
}

export function AdminAuthProvider({ children }: AdminAuthProviderProps) {
  const { ready, authenticated, user, logout: privyLogout, getAccessToken } = usePrivy();

  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [role, setRole] = useState<UserRole>(null);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAdminStatus = useCallback(async () => {
    if (!authenticated) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setRole(null);
      setWalletAddress(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get fresh access token from Privy
      const token = await getAccessToken();

      if (!token) {
        setError('Failed to get authentication token');
        setIsLoading(false);
        return;
      }

      // Store token globally for API requests
      setGlobalAuthToken(token);

      // Check admin status via API
      const response = await apiRequest('GET', '/api/admin/me');

      setIsAdmin(response.isAdmin === true);
      setIsSuperAdmin(response.isSuperAdmin === true);
      setRole(response.role || 'user');
      setWalletAddress(response.walletAddress || null);
      setError(null);
    } catch (err: any) {
      console.error('Admin status check failed:', err);

      // Handle specific error cases
      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        setError('Authentication failed. Please reconnect your wallet.');
      } else if (err.message?.includes('403') || err.message?.includes('Forbidden')) {
        // User is authenticated but not admin - this is expected for non-admins
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setRole('user');
        setError(null);
      } else {
        setError(err.message || 'Failed to verify admin status');
      }

      setIsAdmin(false);
      setIsSuperAdmin(false);
    } finally {
      setIsLoading(false);
    }
  }, [authenticated, getAccessToken]);

  // Extract wallet address from Privy user
  useEffect(() => {
    if (user?.wallet?.address) {
      setWalletAddress(user.wallet.address);
    } else if (user?.linkedAccounts) {
      const walletAccount = user.linkedAccounts.find(
        (account) => account.type === 'wallet'
      );
      if (walletAccount && 'address' in walletAccount) {
        setWalletAddress(walletAccount.address);
      }
    }
  }, [user]);

  // Check admin status when authentication state changes
  useEffect(() => {
    if (ready) {
      checkAdminStatus();
    }
  }, [ready, authenticated, checkAdminStatus]);

  const handleLogout = useCallback(async () => {
    try {
      // Clear global auth token
      setGlobalAuthToken(undefined);

      // Clear local state
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setRole(null);
      setWalletAddress(null);
      setError(null);

      // Logout from Privy
      await privyLogout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [privyLogout]);

  const value: AdminAuthContextValue = {
    isAuthenticated: authenticated,
    isAdmin,
    isSuperAdmin,
    walletAddress,
    role,
    isLoading: !ready || isLoading,
    error,
    logout: handleLogout,
    refreshAdminStatus: checkAdminStatus,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
